import React, { useEffect, useMemo, useRef, useState } from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import api from "../../utils/axios";
import { ColDef } from "ag-grid-enterprise";
import FilterSection from "../../components/FilterOrderSection";
import { useNavigate } from "react-router";
import PaginationTable from "../../components/Pagination";
import "./index.css";
import { valueDateFormatter } from "../../utils/formatters";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import { toast } from "react-toastify";
import useLang from "../../hooks/useLang";
import { useMediaQuery } from "@mui/material";
import { CONFIG } from "../../config";

const buyerValueGetter = (params: any) => {
	const text =
		params.data.buyerNameAndSurname +
		" (" +
		params.data.buyerPhoneNo +
		")" +
		"\n" +
		params.data.city +
		", " +
		params.data.country;
	return text;
};

const codeFormatter = (params: any) => {
	const date = valueDateFormatter(params.data.createdAt);
	const text =
		"#" +
		params.data.displayId +
		" (" +
		params.data.statusDescription +
		") " +
		"\n " +
		date;
	return text;
};

const WaitingPickupOrders = () => {
	const { strings } = useLang("waitingPickupOrders");

	let defaultExcelExportSettings = {};

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef<AgGridReact | null>(null);

	const [tableData, setTableData] = useState([]);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [filteredOrders, setFilteredOrders] = useState([]);
	const navigate = useNavigate();

	const calculateRowHeight = (params: any) => {
		const cellContent =
			params.data.buyerNameAndSurname +
			"\n" +
			"(" +
			params.data.buyerPhoneNo +
			")" +
			"\n" +
			params.data.createdAt;
		const lineHeight = 30;
		const numLines = cellContent.split("\n").length;
		const calculatedHeight = lineHeight * numLines;

		return calculatedHeight;
	};
	const orderDecriptionValueGetter = (params: any) => {
		const text =
			strings?.buyerDetails +
			params.data.orderPrice +
			CONFIG?.CURRENCY +
			"\n" +
			strings?.transport +
			params.data.transportPrice +
			CONFIG?.CURRENCY;
		return text;
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		const getTableData = async () => {
			try {
				const response = await api.get("Order/waiting-pickup-orders");
				if (response.status === 200 && response?.data) {
					const result = response?.data;
					setTableData(result);
					setFilteredOrders(result);
				}
			} catch (error: any) {
				console.log(error);
			}
		};
		getTableData();
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 170,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.kodiHeaderName,
				field: "id",
				valueGetter: codeFormatter,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
					"white-space": "pre",
				}),
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerNameAndSurname",
				headerName: strings?.buyerHeaderName,
				valueGetter: buyerValueGetter,
				cellStyle: { "white-space": "pre" },
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDescriptionHeaderName,
				valueGetter: orderDecriptionValueGetter,
				cellStyle: { "white-space": "pre" },
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [
		setTableColumns,
		strings?.buyerHeaderName,
		strings?.kodiHeaderName,
		strings?.orderDescriptionHeaderName,
	]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		if (colId === "id") {
			await onOrderIdClicked(params?.data);
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const filterOrders = (
		countryId: any,
		cityId: any,
		statusIds: any[],
		id: any,
		orderDescription: any,
		fromDate: any,
		toDate: any,
		filterType: any
	) => {
		const isDateFilter = [
			"registration_date",
			"acceptance_date",
			"closure_date",
		].includes(filterType);
		const data = tableData.filter((item: any) => {
			if (
				(countryId != null && item.countryId !== countryId) ||
				(cityId != null && item.cityId !== cityId) ||
				(statusIds?.length > 0 &&
					!statusIds?.includes(item.statusId)) ||
				(id?.toString()?.trim()?.length && item.id.toString() !== id) ||
				(orderDescription?.toString()?.trim()?.length &&
					!JSON.stringify(item)
						?.toLowerCase()
						?.includes(orderDescription?.toLowerCase()?.trim()))
			) {
				return false;
			}

			if (isDateFilter && fromDate && toDate) {
				const itemDate = new Date(
					filterType === "registration_date"
						? item.createdAt
						: item.pickedUpDate
				);
				const fromDateObj = new Date(fromDate);
				const toDateObj = new Date(toDate);

				if (itemDate < fromDateObj || itemDate > toDateObj) {
					return false;
				}
			}

			return true;
		});
		setFilteredOrders(data);
		setCurrentPage(1);
	};

	const customOptions = [
		{
			label: strings?.customOptions.printTicket,
			value: "print_ticket",
		},
		{
			label: strings?.customOptions.printList,
			value: "print_list",
		},
		{
			label: strings?.customOptions.exportExcel,
			value: "export_excel",
		},
		{
			label: strings?.customOptions.delete,
			value: "delete",
		},
	];

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const onPageChanged = (page: any) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	const paginatedOrders = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredOrders.slice(startIndex, endIndex);
	}, [currentPage, filteredOrders]);

	const totalOrders = filteredOrders.length;
	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex =
		startOrderIndex + Math.min(pageSize, totalOrders - startOrderIndex);

	const selectedColumnIds = [
		"displayId",
		"buyerNameAndSurname",
		"orderDescription",
	];

	const onExportSelectedId = () => {
		const dataToExport = filteredOrders.map((item: any) => {
			const date = valueDateFormatter(item.createdAt);
			const displayId =
				"#" +
				item.displayId +
				" (" +
				item.statusDescription +
				") " +
				"\n " +
				date;
			return {
				displayId,
				buyerNameAndSurname:
					item.buyerNameAndSurname +
					" (" +
					item.buyerPhoneNo +
					")" +
					"\n" +
					item.city +
					", " +
					item.country,
				orderDescription:
					strings?.buyerDetails +
					item.orderPrice +
					CONFIG?.CURRENCY +
					"\n" +
					strings?.transport +
					item.transportPrice +
					CONFIG?.CURRENCY,
			};
		});
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const printTickets = async (format: string) => {
		const ids = (grid.current as any).api
			.getSelectedRows()
			?.map((item: any) => ({
				id: item.displayId,
			}));

		if (!ids?.length) {
			toast.warning(strings?.printTicketsWarning);
			return true;
		}
		const formatA4 = format === "A4";
		try {
			const response = await api.post("Order/print-order-ticket", ids, {
				params: { formatA4 },
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(strings?.popupBlocked);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const printOrderList = async () => {
		const ids = grid?.current?.api.getSelectedRows()?.map((item: any) => {
			return { id: item.id };
		});

		if (!ids || ids.length === 0) {
			toast.warning(strings?.noOrdersSelected);
			return;
		}

		try {
			const response = await api.post("Order/order-list-details", ids, {
				responseType: "blob",
			});

			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(strings?.popupError);
			}
		} catch (error) {
			console.error("Error exporting order list details:", error);
		}
	};

	return (
		<>
			<div className="orderMainContainer">
				<div className="ordersContainer">
					<FilterSection
						title={strings?.filterSectionTitle}
						onFilterChange={filterOrders}
						selectedOptions={customOptions}
						onExportSelectedId={onExportSelectedId}
						showAdminFilters={false}
						onPrintTickets={printTickets}
						onPrintList={printOrderList}
					/>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={filteredOrders.length}
						startOrderIndex={startOrderIndex}
						endOrderIndex={endOrderIndex}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
							width: "100%",
						}}
					>
						<AgGridReact
							rowData={paginatedOrders}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							getRowHeight={calculateRowHeight}
							onCellClicked={onCellClicked}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default WaitingPickupOrders;
